import React, { useState } from 'react';
import { ConfigProvider, App, Card, Steps, Row, Col, Space } from 'antd';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SectionTitle from './text_components/SectionTitle';

const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid #bfdbfe;
  max-width: 800px;
  margin: auto;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-5px);
  }
`;

const FormSection = styled(Card)`
  margin-bottom: 16px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .ant-card-head {
    background: #f8fafc;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }
`;

const StyledButton = styled.button`
  background: #4a90e2;
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 40px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const InputWrapper = styled.div`
  margin-bottom: 16px;
`;

const StyledLabel = styled.label`
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
  color: #374151;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const RadioWrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 16px;
`;

const Form5500Filing = () => {
  const [formData, setFormData] = useState({
    formType: '', // 'short' or 'long'
    // Basic Plan Information
    planName: "Example Plan Name",
    planNumber: "001",
    planYearBeginDate: "2024-01-01",
    planYearEndDate: "2024-12-31",

    // Plan Sponsor Information (Part II - Line 2)
    sponsorName: "Example Sponsor Inc.",
    sponsorEIN: "12-3456789",
    sponsorAddress: "123 Main Street",
    sponsorCity: "Anytown",
    sponsorState: "CA",
    sponsorZip: "90210",
    sponsorPhone: "555-123-4567",
    sponsorBusinessCode: "541611",

    // Plan Administrator Information (Part II - Line 3)
    adminSameAsSponsor: false,
    adminName: "Example TPA Services",
    adminEIN: "98-7654321",
    adminAddress: "456 Compliance Ave",
    adminCity: "Complianceville",
    adminState: "TX",
    adminZip: "73301",
    adminPhone: "555-987-6543",

    // Funding and Benefit Arrangements (Part I - Lines A - D)
    planFundingArrangement: "Insurance",
    planBenefitArrangement: "Insurance",

    // Participant Counts
    totalParticipants: 100,
    totalActiveParticipants: 80,
    retiredOrSeparatedParticipantsReceivingBenefits: 10,
    retiredOrSeparatedParticipantsEntitledToBenefits: 5,
    deceasedParticipants: 2,
    totalParticipantsEndOfYear: 110,

    // Plan Characteristics Codes
    planCharCodes: ["2E", "3B", "3D"],

    // Financial Information
    planAssetsBeginningYear: "1000000",
    planAssetsEndYear: "1200000",
    planLiabilitiesBeginningYear: "0",
    planLiabilitiesEndYear: "0",
    contributionsEmployer: "50000",
    contributionsParticipants: "20000",
    benefitsPaid: "30000",
    administrativeExpenses: "5000",

    // Compliance and Other Questions
    amendedReturn: false,
    shortPlanYear: false,
    finalReturn: false,
    changeInFundingMethod: false,
    collectiveTrustParticipation: false,
    planTerminatedDate: "",

    // Signer Information
    signerName: "John Doe",
    signerTitle: "Plan Administrator",
    signerDate: "2024-11-15"
  });

  const [currentStep, setCurrentStep] = useState(0);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // console.log("Submitting Form 5500 Data:", formData);
    alert("Form 5500 data submitted (dummy submission)");
  };

  const next = () => {
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  // Conditionally render questions based on short or long form
  // Short Form (5500-SF) will skip some detail fields and sections.

  const isLongForm = formData.formType === 'long';

  const steps = [
    {
      title: 'Form Type',
      content: () => (
        <FormSection title="Choose Form Type">
          <RadioWrapper>
            <input
              type="radio"
              name="formType"
              value="short"
              checked={formData.formType === 'short'}
              onChange={handleChange}
            />
            <StyledLabel style={{ display: 'inline', marginBottom: '0' }}>Short Form (5500-SF)</StyledLabel>
          </RadioWrapper>
          <RadioWrapper>
            <input
              type="radio"
              name="formType"
              value="long"
              checked={formData.formType === 'long'}
              onChange={handleChange}
            />
            <StyledLabel style={{ display: 'inline', marginBottom: '0' }}>Long Form (Form 5500)</StyledLabel>
          </RadioWrapper>
        </FormSection>
      )
    },
    {
      title: 'Basic Information',
      content: () => (
        <>
          <FormSection title="Basic Plan Information">
            <InputWrapper>
              <StyledLabel>Plan Name:</StyledLabel>
              <StyledInput
                type="text"
                name="planName"
                value={formData.planName}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Plan Number:</StyledLabel>
              <StyledInput
                type="text"
                name="planNumber"
                value={formData.planNumber}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Plan Year Begin Date:</StyledLabel>
              <StyledInput
                type="date"
                name="planYearBeginDate"
                value={formData.planYearBeginDate}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Plan Year End Date:</StyledLabel>
              <StyledInput
                type="date"
                name="planYearEndDate"
                value={formData.planYearEndDate}
                onChange={handleChange}
              />
            </InputWrapper>
          </FormSection>

          <FormSection title="Plan Sponsor Information">
            <InputWrapper>
              <StyledLabel>Sponsor Name:</StyledLabel>
              <StyledInput
                type="text"
                name="sponsorName"
                value={formData.sponsorName}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Sponsor EIN:</StyledLabel>
              <StyledInput
                type="text"
                name="sponsorEIN"
                value={formData.sponsorEIN}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Sponsor Address:</StyledLabel>
              <StyledInput
                type="text"
                name="sponsorAddress"
                value={formData.sponsorAddress}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Sponsor City:</StyledLabel>
              <StyledInput
                type="text"
                name="sponsorCity"
                value={formData.sponsorCity}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Sponsor State:</StyledLabel>
              <StyledInput
                type="text"
                name="sponsorState"
                value={formData.sponsorState}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Sponsor Zip:</StyledLabel>
              <StyledInput
                type="text"
                name="sponsorZip"
                value={formData.sponsorZip}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Sponsor Phone:</StyledLabel>
              <StyledInput
                type="text"
                name="sponsorPhone"
                value={formData.sponsorPhone}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Sponsor Business Code:</StyledLabel>
              <StyledInput
                type="text"
                name="sponsorBusinessCode"
                value={formData.sponsorBusinessCode}
                onChange={handleChange}
              />
            </InputWrapper>
          </FormSection>

          {/* For the short form, often admin info is simplified or omitted, but let's still show it.
             The long form provides full detail. For short form, we can keep it simple but still show. */}
          <FormSection title="Plan Administrator Information">
            <InputWrapper>
              <StyledLabel>Administrator same as Sponsor:</StyledLabel>
              <input
                type="checkbox"
                name="adminSameAsSponsor"
                checked={formData.adminSameAsSponsor}
                onChange={handleChange}
              />
            </InputWrapper>
            {!formData.adminSameAsSponsor && (
              <>
                <InputWrapper>
                  <StyledLabel>Administrator Name:</StyledLabel>
                  <StyledInput
                    type="text"
                    name="adminName"
                    value={formData.adminName}
                    onChange={handleChange}
                  />
                </InputWrapper>
                {isLongForm && ( // For short form, let's skip some of these details
                  <>
                    <InputWrapper>
                      <StyledLabel>Administrator EIN:</StyledLabel>
                      <StyledInput
                        type="text"
                        name="adminEIN"
                        value={formData.adminEIN}
                        onChange={handleChange}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <StyledLabel>Administrator Address:</StyledLabel>
                      <StyledInput
                        type="text"
                        name="adminAddress"
                        value={formData.adminAddress}
                        onChange={handleChange}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <StyledLabel>Administrator City:</StyledLabel>
                      <StyledInput
                        type="text"
                        name="adminCity"
                        value={formData.adminCity}
                        onChange={handleChange}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <StyledLabel>Administrator State:</StyledLabel>
                      <StyledInput
                        type="text"
                        name="adminState"
                        value={formData.adminState}
                        onChange={handleChange}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <StyledLabel>Administrator Zip:</StyledLabel>
                      <StyledInput
                        type="text"
                        name="adminZip"
                        value={formData.adminZip}
                        onChange={handleChange}
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <StyledLabel>Administrator Phone:</StyledLabel>
                      <StyledInput
                        type="text"
                        name="adminPhone"
                        value={formData.adminPhone}
                        onChange={handleChange}
                      />
                    </InputWrapper>
                  </>
                )}
              </>
            )}
          </FormSection>
        </>
      )
    },
    {
      title: 'Participant Data',
      content: () => (
        <>
          <FormSection title="Participant Counts">
            <InputWrapper>
              <StyledLabel>Total Participants:</StyledLabel>
              <StyledInput
                type="number"
                name="totalParticipants"
                value={formData.totalParticipants}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Total Active Participants:</StyledLabel>
              <StyledInput
                type="number"
                name="totalActiveParticipants"
                value={formData.totalActiveParticipants}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Retired/Separated Receiving Benefits:</StyledLabel>
              <StyledInput
                type="number"
                name="retiredOrSeparatedParticipantsReceivingBenefits"
                value={formData.retiredOrSeparatedParticipantsReceivingBenefits}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Retired/Separated Entitled to Benefits:</StyledLabel>
              <StyledInput
                type="number"
                name="retiredOrSeparatedParticipantsEntitledToBenefits"
                value={formData.retiredOrSeparatedParticipantsEntitledToBenefits}
                onChange={handleChange}
              />
            </InputWrapper>
            {isLongForm && (
              <InputWrapper>
                <StyledLabel>Deceased Participants w/ Beneficiaries Receiving Benefits:</StyledLabel>
                <StyledInput
                  type="number"
                  name="deceasedParticipants"
                  value={formData.deceasedParticipants}
                  onChange={handleChange}
                />
              </InputWrapper>
            )}
            <InputWrapper>
              <StyledLabel>Total Participants at End of Year:</StyledLabel>
              <StyledInput
                type="number"
                name="totalParticipantsEndOfYear"
                value={formData.totalParticipantsEndOfYear}
                onChange={handleChange}
              />
            </InputWrapper>
          </FormSection>

          {isLongForm && (
            <FormSection title="Plan Characteristics Codes (Part II - Line 8)">
              <InputWrapper>
                <StyledLabel>Codes (comma separated):</StyledLabel>
                <StyledInput
                  type="text"
                  name="planCharCodes"
                  value={formData.planCharCodes.join(", ")}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      planCharCodes: e.target.value.split(",").map(code => code.trim())
                    }))
                  }
                />
              </InputWrapper>
            </FormSection>
          )}
        </>
      )
    },
    {
      title: 'Financial Details',
      content: () => (
        <>
          <FormSection title="Funding and Benefit Arrangements">
            <InputWrapper>
              <StyledLabel>Plan Funding Arrangement:</StyledLabel>
              <StyledInput
                type="text"
                name="planFundingArrangement"
                value={formData.planFundingArrangement}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Plan Benefit Arrangement:</StyledLabel>
              <StyledInput
                type="text"
                name="planBenefitArrangement"
                value={formData.planBenefitArrangement}
                onChange={handleChange}
              />
            </InputWrapper>
          </FormSection>

          <FormSection title="Financial Information">
            <InputWrapper>
              <StyledLabel>Plan Assets Beginning of Year:</StyledLabel>
              <StyledInput
                type="number"
                name="planAssetsBeginningYear"
                value={formData.planAssetsBeginningYear}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Plan Assets End of Year:</StyledLabel>
              <StyledInput
                type="number"
                name="planAssetsEndYear"
                value={formData.planAssetsEndYear}
                onChange={handleChange}
              />
            </InputWrapper>
            {/* For short form, we might skip detailed liabilities, just show basic fields */}
            {isLongForm && (
              <>
                <InputWrapper>
                  <StyledLabel>Plan Liabilities Beginning of Year:</StyledLabel>
                  <StyledInput
                    type="number"
                    name="planLiabilitiesBeginningYear"
                    value={formData.planLiabilitiesBeginningYear}
                    onChange={handleChange}
                  />
                </InputWrapper>
                <InputWrapper>
                  <StyledLabel>Plan Liabilities End of Year:</StyledLabel>
                  <StyledInput
                    type="number"
                    name="planLiabilitiesEndYear"
                    value={formData.planLiabilitiesEndYear}
                    onChange={handleChange}
                  />
                </InputWrapper>
              </>
            )}
            <InputWrapper>
              <StyledLabel>Employer Contributions:</StyledLabel>
              <StyledInput
                type="number"
                name="contributionsEmployer"
                value={formData.contributionsEmployer}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Participant Contributions:</StyledLabel>
              <StyledInput
                type="number"
                name="contributionsParticipants"
                value={formData.contributionsParticipants}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Benefits Paid:</StyledLabel>
              <StyledInput
                type="number"
                name="benefitsPaid"
                value={formData.benefitsPaid}
                onChange={handleChange}
              />
            </InputWrapper>
            {isLongForm && (
              <InputWrapper>
                <StyledLabel>Administrative Expenses:</StyledLabel>
                <StyledInput
                  type="number"
                  name="administrativeExpenses"
                  value={formData.administrativeExpenses}
                  onChange={handleChange}
                />
              </InputWrapper>
            )}
          </FormSection>
        </>
      )
    },
    {
      title: 'Review & Submit',
      content: () => (
        <>
          {/* For short form, compliance questions are fewer or simplified */}
          {isLongForm && (
            <FormSection title="Compliance and Other Questions">
              <InputWrapper>
                <StyledLabel>Amended Return:</StyledLabel>
                <input
                  type="checkbox"
                  name="amendedReturn"
                  checked={formData.amendedReturn}
                  onChange={handleChange}
                />
              </InputWrapper>
              <InputWrapper>
                <StyledLabel>Short Plan Year:</StyledLabel>
                <input
                  type="checkbox"
                  name="shortPlanYear"
                  checked={formData.shortPlanYear}
                  onChange={handleChange}
                />
              </InputWrapper>
              <InputWrapper>
                <StyledLabel>Final Return:</StyledLabel>
                <input
                  type="checkbox"
                  name="finalReturn"
                  checked={formData.finalReturn}
                  onChange={handleChange}
                />
              </InputWrapper>
              <InputWrapper>
                <StyledLabel>Change in Funding Method:</StyledLabel>
                <input
                  type="checkbox"
                  name="changeInFundingMethod"
                  checked={formData.changeInFundingMethod}
                  onChange={handleChange}
                />
              </InputWrapper>
              <InputWrapper>
                <StyledLabel>Collective Trust Participation:</StyledLabel>
                <input
                  type="checkbox"
                  name="collectiveTrustParticipation"
                  checked={formData.collectiveTrustParticipation}
                  onChange={handleChange}
                />
              </InputWrapper>
              <InputWrapper>
                <StyledLabel>Plan Terminated Date (if applicable):</StyledLabel>
                <StyledInput
                  type="date"
                  name="planTerminatedDate"
                  value={formData.planTerminatedDate}
                  onChange={handleChange}
                />
              </InputWrapper>
            </FormSection>
          )}

          <FormSection title="Signer Information">
            <InputWrapper>
              <StyledLabel>Signer Name:</StyledLabel>
              <StyledInput
                type="text"
                name="signerName"
                value={formData.signerName}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Signer Title:</StyledLabel>
              <StyledInput
                type="text"
                name="signerTitle"
                value={formData.signerTitle}
                onChange={handleChange}
              />
            </InputWrapper>
            <InputWrapper>
              <StyledLabel>Signer Date:</StyledLabel>
              <StyledInput
                type="date"
                name="signerDate"
                value={formData.signerDate}
                onChange={handleChange}
              />
            </InputWrapper>
          </FormSection>
        </>
      )
    }
  ];

  return (
    <App>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#4a90e2',
            borderRadius: 10,
            fontFamily: "'Inter', sans-serif",
          },
        }}
      >
        <Row justify="center" align="middle" className="min-h-screen py-8">
          <Col xs={24} sm={24} md={22} lg={20} xl={18}>
            <form onSubmit={handleSubmit}>
              <StyledCard>
                <SectionTitle>Form 5500 Filing - Data Entry</SectionTitle>

                <Steps current={currentStep} className="mb-8">
                  {steps.map(item => (
                    <Steps.Step key={item.title} title={item.title} />
                  ))}
                </Steps>

                <div className="steps-content">
                  {steps[currentStep].content()}
                </div>

                <div className="steps-action mt-8">
                  <Space>
                    {currentStep > 0 && (
                      <StyledButton type="button" onClick={prev}>
                        Previous
                      </StyledButton>
                    )}
                    {currentStep < steps.length - 1 && (
                      // Only enable Next if formType is chosen in the first step
                      currentStep === 0 && !formData.formType ? null : (
                        <StyledButton type="button" onClick={next}>
                          Next
                        </StyledButton>
                      )
                    )}
                    {currentStep === steps.length - 1 && (
                      <StyledButton type="submit">
                        Submit Form 5500
                      </StyledButton>
                    )}
                  </Space>
                </div>
              </StyledCard>
            </form>
          </Col>
        </Row>
      </ConfigProvider>
    </App>
  );
};

export default Form5500Filing;
